import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Product, Rubric, SelectedRubric } from "@/lib/types";
import {
  buildSelectedRubrics, componentKey, compositionRubrics, itemKeyFor,
  type ComponentOptions, type Selection,
} from "@/lib/composition";
import { useScrollLock } from "@/lib/scrollLock";
import { flyImageToCart } from "@/lib/flyToCart";
import { withImageVersion } from "@/lib/imageUrl";
import { getTranslatedText } from "@/lib/translations";
import { QtyStepper } from "./QtyStepper";

interface Props {
  product: Product | null;
  /** Rubriques résolues depuis le catalogue (useRubricCatalog / resolveProductRubrics). */
  rubrics: Rubric[];
  /** Catalogue pas encore reçu : on n'affiche pas un produit « sans options » par erreur. */
  rubricsLoading: boolean;
  /** Element the modal was opened from — used as the fly-to-cart source. */
  sourceEl?: Element | null;
  open: boolean;
  /** Mode consultation (showSitInCart faux) : pas de quantité ni d'ajout. */
  readOnly?: boolean;
  onClose: () => void;
  onAdd: (line: { qty: number; unitPrice: number; options: SelectedRubric[] }) => void;
  /** Returns false if adding `qty` more units would exceed any cap. */
  canAddQty?: (qty: number) => boolean;
  /** Max units that can still be added for the current product (all caps combined). */
  maxAddable?: () => number;
  /** Per-product remaining/cap pairs displayed as info pills. */
  productLimits?: {
    roundRemaining: number | null;
    roundCap: number | null;
    totalRemaining: number | null;
    totalCap: number | null;
  };
  /** Produits proposables à cette heure, masqués compris : choix d'un produit composé. */
  products?: Map<string, Product>;
  /** Rubriques d'options d'un produit (catalogue) : options d'un produit choisi dans une composition. */
  resolveOptions?: (product: Product) => Rubric[];
}

/** Plafond de saisie de la tablette (ProductDetail.js:114). */
const MAX_PRODUCT_QTY = 50;
/** Durée de l'animation de sortie de la page d'options (tailwind `slide-out-right`). */
const CUSTOMIZE_OUT_MS = 180;
const EMPTY_PRODUCTS = new Map<string, Product>();

/**
 * Règles de la tablette (ProductDetail.js:155, 343-361) :
 *  - `selectMax === 1` → choix unique (radio) ; `selectMax` ≤ 0 ou absent → sans plafond ;
 *  - `selectMin` ne s'applique que si `necessary` (un `necessary` sans min exige 1 choix).
 */
const rubricRules = (r: Rubric): { min: number; max: number; isRadio: boolean } => {
  const max = r.selectMax && r.selectMax > 0 ? r.selectMax : Infinity;
  const min = r.necessary ? Math.max(1, r.selectMin ?? 0) : 0;
  return { min, max, isRadio: r.selectMax === 1 };
};

const sumSelection = (sel: Record<string, number> | undefined): number =>
  Object.values(sel ?? {}).reduce((s, n) => s + n, 0);

/**
 * Sélection alignée sur la tablette (selectRubric, ProductDetail.js:145-179) :
 *  - radio (selectMax === 1) : le nouveau choix remplace l'ancien ;
 *  - sinon toggle 0/1, refusé au-delà de selectMax (cumul des quantités).
 */
const toggledRubric = (current: Record<string, number> | undefined, rubric: Rubric, itemKey: string): Record<string, number> => {
  const cur = { ...(current || {}) };
  const { max, isRadio } = rubricRules(rubric);
  const currentCount = cur[itemKey] || 0;

  if (isRadio) {
    const wasActive = currentCount > 0;
    for (const k of Object.keys(cur)) cur[k] = 0;
    cur[itemKey] = wasActive ? 0 : 1;
  } else if (currentCount > 0) {
    cur[itemKey] = 0;
  } else if (sumSelection(cur) < max) {
    cur[itemKey] = 1;
  }
  return cur;
};

/** Quantité d'une option `allowSameMultiple` (rubricQuantityAdd/Remove, ProductDetail.js:181-206). */
const withItemQty = (current: Record<string, number> | undefined, rubric: Rubric, itemKey: string, next: number): Record<string, number> => {
  const cur = { ...(current || {}) };
  const { max } = rubricRules(rubric);
  const others = sumSelection(cur) - (cur[itemKey] || 0);
  cur[itemKey] = Math.max(0, Math.min(next, max - others));
  return cur;
};

/** Rubriques dont le minimum obligatoire n'est pas atteint (verifyProduct, ProductDetail.js:343-361). */
const incompleteOf = (rubrics: Rubric[], selection: Selection): Set<string> => {
  const set = new Set<string>();
  for (const r of rubrics) {
    const { min } = rubricRules(r);
    if (min > 0 && sumSelection(selection[r.id]) < min) set.add(r.id);
  }
  return set;
};

const Badge = ({
  variant,
  children,
}: {
  variant: "required" | "neutral";
  children: React.ReactNode;
}): JSX.Element => {
  const cls =
    variant === "required"
      ? "bg-primary/15 text-primary"
      : "bg-bg text-ink-soft border border-border";
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-pill whitespace-nowrap ${cls}`}>
      {children}
    </span>
  );
};

export const ProductDetailModal = ({
  product,
  rubrics,
  rubricsLoading,
  sourceEl,
  open,
  readOnly = false,
  onClose,
  onAdd,
  canAddQty,
  maxAddable,
  productLimits,
  products = EMPTY_PRODUCTS,
  resolveOptions,
}: Props): JSX.Element | null => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [qty, setQty] = useState(1);
  const [selection, setSelection] = useState<Selection>({});
  const heroRef = useRef<HTMLDivElement>(null);
  // Options des produits choisis dans une composition (clé : componentKey), et la page d'options ouverte :
  // `previous` = ses options avant « Modifier », remises sur « Retour ».
  const [components, setComponents] = useState<Record<string, ComponentOptions>>({});
  const [customizing, setCustomizing] = useState<{ rubricId: string; productId: string; previous: ComponentOptions | null } | null>(null);
  const [customizingError, setCustomizingError] = useState(false);
  const [customizingLeaving, setCustomizingLeaving] = useState(false);
  const customizeSectionRefs = useRef<Record<string, HTMLElement | null>>({});

  const allergens = useMemo<string[]>(() => {
    const list = (product as { allergene?: string[] } | null)?.allergene;
    return Array.isArray(list) ? list.filter(Boolean) : [];
  }, [product]);

  // iOS-safe scroll lock — prevent the page behind from scrolling while open.
  useScrollLock(open);

  // Reset state on each modal open or product change
  useEffect(() => {
    if (!open) return;
    setQty(1);
    setSelection({});
    setComponents({});
    setCustomizing(null);
    setCustomizingError(false);
    setCustomizingLeaving(false);
  }, [open, product?.id]);

  // Produit composé : ses étapes sont des rubriques de la fiche, avant ses éventuelles options
  const allRubrics = useMemo(
    () => [...compositionRubrics(product, products), ...rubrics],
    [product, products, rubrics]
  );

  // Build the cart payload with the real Firestore ids when available.
  const selectedRubrics: SelectedRubric[] = useMemo(
    () => buildSelectedRubrics(allRubrics, selection, components),
    [allRubrics, selection, components]
  );

  const incompleteRubrics = useMemo(() => incompleteOf(allRubrics, selection), [allRubrics, selection]);
  const componentsIncomplete = Object.values(components).some((c) => incompleteOf(c.rubrics, c.selection).size > 0);

  const hasErrors = incompleteRubrics.size > 0 || componentsIncomplete;
  const overLimit = canAddQty ? !canAddQty(qty) : false;

  // Live total: product unit price + sum of selected rubric items (× quantité d'option)
  const extrasTotal = selectedRubrics.reduce(
    (s, r) =>
      s +
      r.data.reduce((ss, it) => ss + (it.unitPrice || 0) * (it.quantity || 1), 0),
    0
  );
  const baseUnitPrice = product?.unitPrice ?? 0;
  const lineTotal = (baseUnitPrice + extrasTotal) * qty;

  // Les options d'un produit qui n'est plus choisi dans son étape s'en vont avec lui
  const dropUnchosenComponents = (rubric: Rubric, next: Record<string, number>): void => {
    setComponents((prev) => {
      const out = { ...prev };
      for (const key of Object.keys(out)) {
        const [rubricId, productId] = key.split("|");
        if (rubricId === rubric.id && !((next[productId] || 0) > 0)) delete out[key];
      }
      return out;
    });
  };

  const toggleItem = (rubric: Rubric, itemKey: string): void => {
    if (!rubric.stepId) {
      setSelection((prev) => ({ ...prev, [rubric.id]: toggledRubric(prev[rubric.id], rubric, itemKey) }));
      return;
    }
    // Étape de composition (clé = id du produit) : un produit choisi apporte ses options, réglées aussitôt
    // sur sa propre page
    const wasChosen = (selection[rubric.id]?.[itemKey] || 0) > 0;
    const next = toggledRubric(selection[rubric.id], rubric, itemKey);
    setSelection((prev) => ({ ...prev, [rubric.id]: next }));
    dropUnchosenComponents(rubric, next);
    const chosenProduct = products.get(itemKey);
    if (wasChosen || !((next[itemKey] || 0) > 0) || !chosenProduct || !resolveOptions) return;
    const optionRubrics = resolveOptions(chosenProduct);
    if (!optionRubrics.length) return;
    setComponents((prev) => ({ ...prev, [componentKey(rubric.id, itemKey)]: { rubrics: optionRubrics, selection: {} } }));
    openCustomizing(rubric.id, itemKey, null);
  };

  const setItemQty = (rubric: Rubric, itemKey: string, next: number): void => {
    const updated = withItemQty(selection[rubric.id], rubric, itemKey, next);
    setSelection((prev) => ({ ...prev, [rubric.id]: updated }));
    if (rubric.stepId) dropUnchosenComponents(rubric, updated);
  };

  // --- page d'options d'un produit choisi dans une composition ---

  const openCustomizing = (rubricId: string, productId: string, previous: ComponentOptions | null): void => {
    customizeSectionRefs.current = {};
    setCustomizingError(false);
    setCustomizingLeaving(false);
    setCustomizing({ rubricId, productId, previous });
  };

  const customizingKey = customizing ? componentKey(customizing.rubricId, customizing.productId) : null;
  const customizingOptions = customizingKey ? components[customizingKey] : undefined;

  // La page repart vers la droite, puis disparaît
  const leaveCustomizing = (then?: () => void): void => {
    setCustomizingLeaving(true);
    window.setTimeout(() => {
      then?.();
      setCustomizing(null);
      setCustomizingLeaving(false);
      setCustomizingError(false);
    }, CUSTOMIZE_OUT_MS);
  };

  const confirmCustomizing = (): void => {
    if (!customizingOptions) return leaveCustomizing();
    const missing = customizingOptions.rubrics.find((r) => incompleteOf([r], customizingOptions.selection).size > 0);
    if (missing) {
      setCustomizingError(true);
      customizeSectionRefs.current[missing.id]?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    leaveCustomizing();
  };

  // Retour : un nouveau choix est retiré ; après « Modifier », les options d'avant sont remises
  const cancelCustomizing = (): void => {
    if (!customizing || !customizingKey) return;
    const { rubricId, productId, previous } = customizing;
    const key = customizingKey;
    leaveCustomizing(() => {
      if (previous) {
        setComponents((prev) => ({ ...prev, [key]: previous }));
        return;
      }
      setSelection((prev) => ({ ...prev, [rubricId]: { ...(prev[rubricId] || {}), [productId]: 0 } }));
      setComponents((prev) => {
        const out = { ...prev };
        delete out[key];
        return out;
      });
    });
  };

  const updateComponent = (update: (c: ComponentOptions) => ComponentOptions): void => {
    if (!customizingKey) return;
    const key = customizingKey;
    setComponents((prev) => (prev[key] ? { ...prev, [key]: update(prev[key]) } : prev));
  };

  // Une rubrique (ou une étape de composition) et ses éléments
  const renderRubric = (
    r: Rubric,
    sel: Record<string, number>,
    { incomplete, onToggle, onSetQty, sectionRef }: {
      incomplete: boolean;
      onToggle: (key: string) => void;
      onSetQty: (key: string, n: number) => void;
      sectionRef?: (el: HTMLElement | null) => void;
    }
  ): JSX.Element => {
    const { min, max, isRadio } = rubricRules(r);
    const selectMin = r.selectMin ?? 0;
    const selectMax = r.selectMax ?? 0;
    // Badges : mêmes conditions que la tablette (ProductDetail.js:760-763).
    const isExactly = selectMin > 1 && selectMin === selectMax;
    const showRequired = !!r.necessary && !isExactly;
    const showMin = !isExactly && !!r.necessary && selectMin > 1;
    const showMax = !isExactly && selectMax > 1 && selectMax !== selectMin;
    const rubricName = getTranslatedText(r, "rubricName", lang);
    const visibleItems = r.data
      .map((it, idx) => ({ it, key: itemKeyFor(it, idx) }))
      .filter(({ it }) => it.displayApp !== false);
    return (
      <section key={r.id} className="mt-7" ref={sectionRef}>
        <header className="flex items-center justify-between gap-2 mb-3">
          <h3 className="display-font text-lg font-semibold text-ink leading-tight">
            {rubricName}
          </h3>
          <div className="flex flex-wrap items-center gap-1.5 shrink-0">
            {isExactly && (
              <Badge variant="required">
                {t("modalProductDetail.exactly", { count: selectMin, defaultValue: `Exactement ${selectMin}` })}
              </Badge>
            )}
            {showRequired && (
              <Badge variant="required">{t("modalProductDetail.required", "Obligatoire")}</Badge>
            )}
            {showMin && (
              <Badge variant="neutral">
                {t("modalProductDetail.min", { count: selectMin, defaultValue: `min. ${selectMin}` })}
              </Badge>
            )}
            {showMax && (
              <Badge variant="neutral">
                {t("modalProductDetail.max", { count: selectMax, defaultValue: `max. ${selectMax}` })}
              </Badge>
            )}
          </div>
        </header>
        {incomplete && (
          <p className="text-xs text-danger mb-2" role="status">
            {min > 1
              ? t("modalProductDetail.min", { count: min, defaultValue: `min. ${min}` })
              : t("error.pleaseSelect", "Veuillez selectionner les choix obligatoires")}
          </p>
        )}
        <ul className="space-y-2">
          {visibleItems.map(({ it, key }) => {
            const count = sel[key] || 0;
            const active = count > 0;
            const orderable = it.canOrder !== false;
            const itemName = getTranslatedText(it, "name", lang);
            const showStepper = !!r.allowSameMultiple && active && !isRadio;
            return (
              <li key={key}>
                <div
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-button border-2 transition-colors ${
                    !orderable
                      ? "border-border bg-surface opacity-50"
                      : active
                        ? "border-primary bg-pink-softer"
                        : "border-border bg-surface hover:border-warm/60"
                  }`}
                >
                  <button
                    type="button"
                    disabled={!orderable}
                    onClick={() => onToggle(key)}
                    className="flex items-center gap-3 min-w-0 flex-1 text-left disabled:cursor-not-allowed"
                    role={isRadio ? "radio" : "checkbox"}
                    aria-checked={active}
                  >
                    <span
                      className={`flex-shrink-0 w-5 h-5 ${
                        isRadio ? "rounded-full" : "rounded"
                      } border-2 flex items-center justify-center text-[11px] leading-none ${
                        active
                          ? "border-primary bg-primary text-white"
                          : "border-ink-muted"
                      }`}
                      aria-hidden="true"
                    >
                      {active ? (isRadio ? "●" : "✓") : ""}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-ink truncate">{itemName}</span>
                      {!orderable && (
                        <span className="block text-[11px] text-ink-muted">
                          {t("product.outOfOrder", "Actuellement indisponible")}
                        </span>
                      )}
                    </span>
                  </button>
                  <span className="flex items-center gap-2 shrink-0">
                    {showStepper && (
                      <QtyStepper
                        value={count}
                        onChange={(n) => onSetQty(key, n)}
                        min={0}
                        max={Number.isFinite(max) ? max : 99}
                        size="sm"
                      />
                    )}
                    {it.unitPrice > 0 && (
                      <span className="text-ink-soft tabular-nums">
                        +{it.unitPrice.toFixed(2)} €
                      </span>
                    )}
                  </span>
                </div>
                {r.stepId && active && renderChosenOptions(r, key, it.name)}
              </li>
            );
          })}
        </ul>
      </section>
    );
  };

  // Sous un produit choisi dans une étape : ses options, et de quoi y revenir
  const renderChosenOptions = (r: Rubric, productId: string, name: string): JSX.Element | null => {
    const c = components[componentKey(r.id, productId)];
    if (!c) return null;
    const names = buildSelectedRubrics(c.rubrics, c.selection).flatMap((s) => s.data.map((d) => d.name));
    return (
      <div className="flex flex-wrap items-center gap-2 pl-12 pt-1.5">
        {names.length > 0 && <span className="text-xs text-ink-soft">{names.join(" · ")}</span>}
        <button
          type="button"
          onClick={() => openCustomizing(r.id, productId, c)}
          className="text-xs font-semibold text-primary border border-primary rounded-pill px-3 py-1"
          aria-label={`${t("modalProductDetail.editOptions", "Modifier")} ${name}`}
        >
          {t("modalProductDetail.editOptions", "Modifier")}
        </button>
      </div>
    );
  };

  // Options d'un produit choisi : une page à lui par-dessus la fiche (photo, provenance, nom, toutes ses
  // options), avec son propre retour et sa validation — pas de fermeture pendant ce temps
  const renderCustomizingPage = (): JSX.Element | null => {
    if (!customizing || !customizingOptions || !product) return null;
    const step = allRubrics.find((r) => r.id === customizing.rubricId);
    const chosen = products.get(customizing.productId);
    const chosenName = chosen ? getTranslatedText(chosen, "name", lang) : "";
    const chosenDescription = chosen?.description ? getTranslatedText(chosen, "description", lang) : "";
    const missing = incompleteOf(customizingOptions.rubrics, customizingOptions.selection);
    return (
      <div
        className={`absolute inset-0 z-10 bg-bg flex flex-col ${customizingLeaving ? "animate-slide-out-right" : "animate-slide-in-right"}`}
      >
        <div className="flex-1 overflow-y-auto px-5 pt-4 pb-6">
          <button
            type="button"
            onClick={cancelCustomizing}
            className="inline-flex items-center gap-2 rounded-pill border border-border bg-surface px-4 py-2 font-semibold text-ink"
          >
            <span aria-hidden="true">←</span> {t("modalProductDetail.back", "Retour")}
          </button>
          {chosen?.image?.path && (
            <img
              src={withImageVersion(chosen.image.path)}
              alt=""
              decoding="async"
              className="mt-4 w-full rounded-modal object-cover"
              style={{ aspectRatio: "4 / 3" }}
            />
          )}
          <p className="mt-4 text-sm text-ink-muted">
            {getTranslatedText(product, "name", lang)} · {step ? getTranslatedText(step, "rubricName", lang) : ""}
          </p>
          <h2 className="display-font text-2xl font-semibold text-ink leading-tight">{chosenName}</h2>
          {chosenDescription && <p className="text-ink-soft mt-2 whitespace-pre-line">{chosenDescription}</p>}
          <h3 className="display-font text-lg font-semibold text-ink mt-6">
            {t("modalProductDetail.customizeTitle", "Personnalisez votre choix")}
          </h3>
          {customizingOptions.rubrics.map((r) =>
            renderRubric(r, customizingOptions.selection[r.id] || {}, {
              incomplete: customizingError && missing.has(r.id),
              onToggle: (key) => updateComponent((c) => ({ ...c, selection: { ...c.selection, [r.id]: toggledRubric(c.selection[r.id], r, key) } })),
              onSetQty: (key, n) => updateComponent((c) => ({ ...c, selection: { ...c.selection, [r.id]: withItemQty(c.selection[r.id], r, key, n) } })),
              sectionRef: (el) => { customizeSectionRefs.current[r.id] = el; },
            })
          )}
          <button
            type="button"
            onClick={confirmCustomizing}
            className={`mt-8 w-full rounded-button py-4 px-5 font-semibold shadow-warm ${missing.size ? "bg-border text-ink-muted" : "bg-primary text-white hover:bg-primary-hover"}`}
          >
            {t("modalProductDetail.confirmOptions", "Valider")}
          </button>
        </div>
      </div>
    );
  };

  if (!open || !product) return null;

  const productName = getTranslatedText(product, "name", lang);
  const productDescription = product.description
    ? getTranslatedText(product, "description", lang)
    : "";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Détail ${productName}`}
      className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label={t("common.close", "Fermer")}
        onClick={customizing ? undefined : onClose}
        className="absolute inset-0 bg-ink/50 backdrop-blur-md animate-fade-in"
      />

      {/*
        max-height en dvh (viewport dynamique) : sur iOS Safari, `vh` mesure le
        viewport barres d'outils repliées. Avec beaucoup d'options la feuille
        atteignait 92vh et son haut — donc la croix de fermeture — passait
        sous la barre d'adresse. La classe `max-h-[92vh]` reste en repli pour
        les navigateurs sans `dvh` (déclaration inline ignorée si non supportée).
      */}
      <div
        className="relative w-full sm:max-w-md bg-bg rounded-t-modal sm:rounded-modal max-h-[92vh] flex flex-col overflow-hidden animate-slide-up shadow-warm-lg"
        style={{ maxHeight: "92dvh" }}
      >
        {/* Hero photo */}
        <div
          ref={heroRef}
          className="relative w-full flex-shrink-0 overflow-hidden"
          style={{ aspectRatio: "4 / 3" }}
        >
          <div className="block" style={{ paddingTop: "75%" }} aria-hidden="true" />
          {product.image?.path ? (
            <img
              src={withImageVersion(product.image.path)}
              alt=""
              width={400}
              height={300}
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 placeholder-warm" aria-hidden="true" />
          )}
          {!customizing && (
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 w-10 h-10 rounded-full bg-surface/95 backdrop-blur-sm shadow-warm flex items-center justify-center text-ink text-lg font-light"
              aria-label={t("common.close", "Fermer")}
            >
              ✕
            </button>
          )}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 pt-5 pb-2">
          <h2 className="display-font text-2xl font-semibold text-ink leading-tight">
            {productName}
          </h2>
          {productDescription && (
            <p className="text-ink-soft mt-2 whitespace-pre-line">{productDescription}</p>
          )}

          {(productLimits?.roundCap != null ||
            productLimits?.totalCap != null) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {productLimits.roundCap != null && (
                <span className="inline-flex items-center gap-1 bg-warm/10 border border-warm/30 rounded-pill px-2.5 py-1 text-[11px] font-medium text-ink">
                  {t("modalProductDetail.leftPerTurn", "Restant par envoie")}:{" "}
                  <span className="tabular-nums">
                    {productLimits.roundRemaining} / {productLimits.roundCap}
                  </span>
                </span>
              )}
              {productLimits.totalCap != null && (
                <span className="inline-flex items-center gap-1 bg-warm/10 border border-warm/30 rounded-pill px-2.5 py-1 text-[11px] font-medium text-ink">
                  {t("modalProductDetail.leftTotal", "Restant total")}:{" "}
                  <span className="tabular-nums">
                    {productLimits.totalRemaining} / {productLimits.totalCap}
                  </span>
                </span>
              )}
            </div>
          )}

          {allergens.length > 0 && (
            <section className="mt-3" aria-label={t("modalAllergen.title", "Informations sur les allergènes")}>
              <h3 className="text-[10px] uppercase tracking-wider text-ink-muted mb-1.5">
                {t("modalAllergen.title", "Informations sur les allergènes")}
              </h3>
              <ul className="flex flex-wrap gap-1.5">
                {allergens.map((a) => (
                  <li
                    key={a}
                    className="inline-flex items-center gap-1 bg-warm/15 text-ink rounded-pill px-2.5 py-0.5 text-[11px] font-medium"
                  >
                    <span aria-hidden="true">⚠</span>
                    <span>{t(`modalAllergen.${a}`, a)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {rubricsLoading && (
            <div className="text-center py-8 text-ink-muted">
              {t("modalProductDetail.loadingOptions", "Chargement des options…")}
            </div>
          )}

          {!rubricsLoading &&
            allRubrics.map((r) =>
              renderRubric(r, selection[r.id] || {}, {
                incomplete: incompleteRubrics.has(r.id),
                onToggle: (key) => toggleItem(r, key),
                onSetQty: (key, n) => setItemQty(r, key, n),
              })
            )}

          {/* Quantity stepper */}
          {!readOnly && (
            <section className="mt-8 flex flex-col items-center">
              <h3 className="display-font text-lg font-semibold text-ink mb-3">
                {t("modalProductDetail.quantity", "Quantité")}
              </h3>
              <QtyStepper
                value={qty}
                onChange={setQty}
                min={1}
                max={Math.max(1, Math.min(MAX_PRODUCT_QTY, maxAddable ? maxAddable() : MAX_PRODUCT_QTY))}
                size="lg"
              />
            </section>
          )}
        </div>

        {/* Sticky CTA */}
        {!readOnly && (
          <div className="border-t border-border p-4 bg-surface">
            {overLimit && (
              <p className="text-xs text-danger text-center mb-2">
                {t("error.maxTotal", "La quantité maximum pour ce produit est atteinte")}
              </p>
            )}
            <button
              type="button"
              disabled={hasErrors || overLimit || rubricsLoading}
              onClick={() => {
                if (!product) return;
                flyImageToCart(
                  sourceEl ?? heroRef.current,
                  withImageVersion(product.image?.path),
                  productName
                );
                // Prix de base seul : les options (et leur prix) voyagent dans `options`.
                onAdd({ qty, unitPrice: baseUnitPrice, options: selectedRubrics });
              }}
              className="w-full bg-primary text-white rounded-button py-4 px-5 font-semibold shadow-warm flex items-center justify-between disabled:opacity-40 transition-colors hover:bg-primary-hover"
            >
              <span>{t("modalProductDetail.add", "Ajouter")}</span>
              <span className="tabular-nums">{lineTotal.toFixed(2)} €</span>
            </button>
          </div>
        )}

        {renderCustomizingPage()}
      </div>
    </div>
  );
};

